import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import Users_no_row, { PricingCard } from './users_row';
import Pagination from '@/components/ui/pagination';
import { userService } from "../../../_services";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Loader2 } from "lucide-react";
import ConfirmDialog from "@/components/ui/confirm-dialog";

/**
 * Pricing Tool Management Page
 * Modernized with shadcn components - Manage pricing tool requests and status
 */
function PricingPage() {
    const searchInputRef = useRef(null);

    // State
    const [pricingData, setPricingData] = useState([]);
    const [allPricingData, setAllPricingData] = useState([]);
    const [pageOfItems, setPageOfItems] = useState([]);
    const [searchInput, setSearchInput] = useState("");
    const [loading, setLoading] = useState(true);

    // Modal states
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [confirmAction, setConfirmAction] = useState({
        title: '',
        type: '',
        repId: '',
        requestId: '',
        requestType: ''
    });

    useEffect(() => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId, userName] = Usermeta_Data.split(':');
        const userRole = 'Admin'; // Set appropriate role
        getPricingToolRequests(userId, userName, userRole);
    }, []);

    const getPricingToolRequests = async (userId, userName, userRole) => {
        setLoading(true);
        try {
            // Updated to use dev-v3 signature with UserRole
            const apidata = await userService.GetPricingToolRequest(userId, userName, userRole);
            if (apidata && apidata.length > 0) {
                setPricingData(apidata);
                setAllPricingData(apidata);
            }
        } catch (error) {
            console.error('Error fetching pricing tool requests:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        const searchText = (value || searchInput).toLowerCase();
        
        if (!searchText) {
            setPricingData(allPricingData);
            return;
        }

        const filtered = allPricingData.filter((item) => {
            const groupNumber = item.groupNumber?.toLowerCase() || '';
            const fullName = item.fullName?.toLowerCase() || '';

            return (
                groupNumber.includes(searchText) ||
                fullName.includes(searchText)
            );
        });

        setPricingData(filtered.length > 0 ? filtered : [{}]);
    };

    const handlePricingAction = async (selectedUserId, requestId, status) => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId, userName] = Usermeta_Data.split(':');

        try {
            // Updated to use dev-v3 signature: (UserId, UserName, SelectedUserId, Status)
            await userService.ManagePricingTool(userId, userName, selectedUserId, status);
            refreshPricingList();
        } catch (error) {
            console.error('Error managing pricing tool:', error);
        }
    };

    const handleActivateDeactivateAction = (repId, requestId, requestType) => {
        setConfirmAction({
            title: requestType,
            type: requestType,
            repId: repId,
            requestId: requestId,
            requestType: requestType
        });
        setShowConfirmDialog(true);
    };

    const handleConfirmAction = async () => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId, userName] = Usermeta_Data.split(':');

        setConfirmLoading(true);
        try {
            await userService.ManagePricingTool(
                userId, 
                userName, 
                confirmAction.repId,
                confirmAction.requestId,
                confirmAction.requestType
            );
            setShowConfirmDialog(false);
            refreshPricingList();
        } catch (error) {
            console.error('Error managing pricing tool:', error);
        } finally {
            setConfirmLoading(false);
        }
    };

    const refreshPricingList = () => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId, userName] = Usermeta_Data.split(':');
        getPricingToolRequests(userId, userName, 'Admin');
    };

    const onChangePage = (pageOfItems) => {
        setPageOfItems(pageOfItems);
    };

    // Auth check
    if (userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate replace to="/signup" />;
    }

    if (window.atob(localStorage.getItem('UserRole')) !== "Admin") {
        return <Navigate replace to="/page-not-found" />;
    }

    localStorage.setItem('pagename', 'pricingpage');

    const dataToDisplay = searchInput.length ? pricingData : pageOfItems;

    return (
        <div className="w-full space-y-6">
            {/* Heading */}
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Pricing Tool</h1>
            
            {/* Search */}
            <div className="relative w-full md:w-96">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary/50" />
                <Input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search by name or group number..."
                    value={searchInput}
                    onChange={(e) => {
                        setSearchInput(e.target.value);
                        handleSearch(e.target.value);
                    }}
                    disabled={loading || !allPricingData.length}
                    className="pl-10"
                />
            </div>
            
            {/* -- Desktop table (md+) ----------------------------------------- */}
            <Card className="hidden md:block">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-accent" />
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto border-b border-border">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="text-primary">Full Name</TableHead>
                                            <TableHead className="text-primary">Group Number</TableHead>
                                            <TableHead className="text-primary">Domain</TableHead>
                                            <TableHead className="text-primary">Status</TableHead>
                                            <TableHead className="text-primary text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {dataToDisplay.map((pricingRow, index) => (
                                            <Users_no_row
                                                key={pricingRow.requestId || index}
                                                index={index}
                                                {...pricingRow}
                                                pricing_action={handlePricingAction}
                                                activate_deactivate_action={handleActivateDeactivateAction}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                            <div className="px-4 py-4">
                                <Pagination
                                    items={pricingData}
                                    onChangePage={onChangePage}
                                    showInfo={true}
                                />
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* -- Mobile cards (<md) ------------------------------------------ */}
            <div className="md:hidden space-y-3">
                {loading ? (
                    <Card>
                        <CardContent className="flex items-center justify-center p-4 py-12 sm:p-6 sm:py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-accent" />
                        </CardContent>
                    </Card>
                ) : dataToDisplay.length > 0 ? (
                    dataToDisplay.map((pricingRow, index) => (
                        <PricingCard
                            key={pricingRow.requestId || index}
                            {...pricingRow}
                            pricing_action={handlePricingAction}
                            activate_deactivate_action={handleActivateDeactivateAction}
                        />
                    ))
                ) : (
                    <Card>
                        <CardContent className="text-center p-4 py-8 sm:p-6 sm:py-8 text-muted-foreground">
                            No records found
                        </CardContent>
                    </Card>
                )}

                {!searchInput && (
                    <div className="pt-2">
                        <Pagination
                            items={pricingData}
                            onChangePage={onChangePage}
                            showInfo={true}
                        />
                    </div>
                )}
            </div>

            {/* Confirm Dialog */}
            <ConfirmDialog
                open={showConfirmDialog}
                onOpenChange={(open) => !open && !confirmLoading && setShowConfirmDialog(false)}
                onConfirm={handleConfirmAction}
                title={`${confirmAction.title} Pricing Tool`}
                description={`Are you sure you want to ${confirmAction.type.toLowerCase()} this pricing tool?`}
                confirmText="Yes"
                cancelText="No"
                variant={confirmAction.type === "Deactivate" ? "destructive" : "warning"}
                loading={confirmLoading}
            />
        </div>
    );
}

export default PricingPage;

